import { Pencil, Trash2, Archive, ArchiveRestore, Camera } from "lucide-react";
import { fmtMoney, statusTone } from "@/lib/format";
import WatchThumb from "@/components/WatchThumb";

const MEDIA_FIELDS = [
  "photo_before_front", "photo_before_back",
  "photo_after_front", "photo_after_back",
  "photo_movement_disassembled", "video_movement",
];
function mediaCount(w) {
  return MEDIA_FIELDS.reduce((n, k) => n + (w[k] ? 1 : 0), 0);
}
function isShowcaseReady(w) {
  const before = !!(w.photo_before_front || w.photo_before_back);
  const after = !!(w.photo_after_front || w.photo_after_back);
  return before && after;
}

function plClass(v) {
  if (v > 0) return "text-emerald-400";
  if (v < 0) return "text-rose-400";
  return "text-[#3A2A10]";
}

/**
 * Table row. `density`:
 *   "compact"      — Mac/PC default. py-1.5, px-3. ~28px row height.
 *   "comfortable"  — Tablet default. py-3, px-4. ~44px row height.
 */
export default function InventoryRow({ watch, density = "comfortable", onOpen, onEdit, onDelete, onArchive }) {
  const w = watch;
  const archived = !!w.archived;
  const compact = density === "compact";
  const cellPad = compact ? "px-3 py-1.5" : "px-4 py-3";
  const badgePad = compact ? "px-1.5 py-0.5" : "px-2 py-1";
  return (
    <tr
      onClick={() => onOpen(w)}
      className={`border-b border-[#8B6A1F]/30 hover:bg-[#2B2214]/[0.06] transition-colors group cursor-pointer ${archived ? "opacity-60" : ""}`}
      data-testid={`row-${w.id}`}
    >
      <td className={`${cellPad} font-mono-data text-[#3A2A10] whitespace-nowrap`} data-testid={`cell-id-${w.id}`}>
        <div className="flex items-center gap-1.5">
          {w.excel_id ?? "—"}
          {archived && (
            <span
              className="text-[8px] uppercase tracking-wider px-1 py-0.5 bg-[#2B2214]/[0.15] border border-[#8B6A1F]/45 rounded-sm text-[#3A2A10]"
              title="This watch is archived"
              data-testid={`archived-badge-${w.id}`}
            >
              Arch
            </span>
          )}
        </div>
      </td>
      <td className={`${compact ? "px-1.5 py-1" : "px-2 py-2"} w-10`}>
        <WatchThumb watch={w} size="xs" />
      </td>
      <td className={`${cellPad} whitespace-nowrap`}>
        <div className="text-[#0A0604] group-hover:text-[#3A2A10] transition-colors">{w.brand || "—"}</div>
      </td>
      <td className={`${cellPad} text-[#3A2A10] max-w-[240px] truncate`} title={w.model || ""}>{w.model || "—"}</td>
      <td className={`${cellPad} font-mono-data text-[#0A0604] whitespace-nowrap`}>{w.caliber || "—"}</td>
      <td className={`${cellPad} text-[#3A2A10]`}>{w.mfg || "—"}</td>
      <td className={`${cellPad} font-mono-data text-[#3A2A10]`}>{w.year_mfg || "—"}</td>
      <td className={`${cellPad} text-[#3A2A10]`}>{w.movement_type || "—"}</td>
      <td className={`${cellPad} font-mono-data text-[#3A2A10]`}>{w.bph || "—"}</td>
      <td className={`${cellPad} font-mono-data text-[#3A2A10]`}>{w.jewels ?? "—"}</td>
      <td className={`${cellPad} text-[#3A2A10] max-w-[140px] truncate`} title={w.shock || ""}>{w.shock || "—"}</td>
      <td className={cellPad}>
        {w.status ? (
          <span className={`inline-block text-[10px] uppercase tracking-wider ${badgePad} border rounded-sm ${statusTone(w.status)}`}>{w.status}</span>
        ) : "—"}
      </td>
      <td className={`${cellPad} text-[#3A2A10] max-w-[160px] truncate`} title={w.bin_location || ""}>{w.bin_location || "—"}</td>
      <td className={`${cellPad} font-mono-data text-[#0A0604] text-right`}>{fmtMoney(w.all_in)}</td>
      <td className={`${cellPad} font-mono-data text-[#0A0604] text-right`} data-testid={`parts-${w.id}`}>{fmtMoney(w.parts)}</td>
      <td className={`${cellPad} font-mono-data text-[#0A0604] text-right font-semibold`} data-testid={`total-${w.id}`}>
        {fmtMoney((Number(w.all_in) || 0) + (Number(w.parts) || 0))}
      </td>
      <td className={`${compact ? "px-2 py-1.5" : "px-3 py-3"} text-center`} data-testid={`pics-${w.id}`}>
        {(() => {
          const n = mediaCount(w);
          const ready = isShowcaseReady(w);
          return (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data rounded-sm border whitespace-nowrap ${
                ready
                  ? "bg-[#D4AF37]/15 text-[#0A0604] border-[#D4AF37]/50"
                  : n > 0
                  ? "bg-[#2B2214]/[0.06] text-[#3A2A10] border-[#8B6A1F]/35"
                  : "text-[#8B6A1F]/60 border-transparent"
              }`}
              title={ready ? "Has before & after pics — ready to share" : `${n}/6 media slots filled`}
            >
              <Camera size={10} /> {n}/6
            </span>
          );
        })()}
      </td>
      <td className={`${cellPad} font-mono-data text-[#0A0604] text-right`}>{fmtMoney(w.sale_price)}</td>
      <td className={`${cellPad} font-mono-data text-right ${plClass(w.profit_loss)}`}>
        {fmtMoney(w.profit_loss)}
      </td>
      <td className={`${cellPad} whitespace-nowrap text-right`}>
        <div className={`flex items-center justify-end ${compact ? "gap-0.5" : "gap-1"} opacity-60 group-hover:opacity-100 transition-opacity`}>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(w); }}
            className={`${compact ? "p-1" : "p-1.5"} text-[#3A2A10] transition-colors`}
            data-testid={`edit-${w.id}`}
            aria-label="Edit watch"
          >
            <Pencil size={compact ? 13 : 14} />
          </button>
          {onArchive && (
            <button
              onClick={(e) => { e.stopPropagation(); onArchive(w); }}
              className={`${compact ? "p-1" : "p-1.5"} text-[#3A2A10] hover:text-[#D4AF37] transition-colors`}
              data-testid={archived ? `unarchive-${w.id}` : `archive-${w.id}`}
              aria-label={archived ? "Restore from archive" : "Archive watch"}
              title={archived ? "Restore from archive" : "Archive (hide but keep record)"}
            >
              {archived ? <ArchiveRestore size={compact ? 13 : 14} /> : <Archive size={compact ? 13 : 14} />}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(w); }}
            className={`${compact ? "p-1" : "p-1.5"} text-[#3A2A10] hover:text-rose-400 transition-colors`}
            data-testid={`delete-${w.id}`}
            aria-label="Delete watch"
          >
            <Trash2 size={compact ? 13 : 14} />
          </button>
        </div>
      </td>
    </tr>
  );
}
